import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/adminService';
import { 
  Calendar, Download, AlertCircle, CheckCircle, XCircle, Clock, X,
  TrendingUp, BarChart3, Users, Wrench, DollarSign, ChevronDown
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ServiceReport = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  const [showDatePicker, setShowDatePicker] = useState(false); // date/time modal
  const [datePickerDraft, setDatePickerDraft] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
  });
  const [datePickerError, setDatePickerError] = useState('');
  const [showProfit, setShowProfit] = useState(false);
  const STATUS_FILTER_STORAGE_KEY = 'roadserve_service_report_status_filter_v1';
  const [statusFilterOpen, setStatusFilterOpen] = useState(false);
  const statusFilterRef = useRef(null);
  const [selectedStatuses, setSelectedStatuses] = useState(null); // array of normalized status keys, null = not initialized yet
  const [pincodeModal, setPincodeModal] = useState({
    open: false,
    mode: null, // 'district' | 'state'
    label: '',
    rows: [],
  });
  const [editPincode, setEditPincode] = useState({
    open: false,
    pincode: '',
    district: '',
    state: '',
  });
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [stateSuggestions, setStateSuggestions] = useState([]);
  const [isLoadingDistrictSuggestions, setIsLoadingDistrictSuggestions] = useState(false);
  const [isLoadingStateSuggestions, setIsLoadingStateSuggestions] = useState(false);
  const [missingPincodeModal, setMissingPincodeModal] = useState({
    open: false,
  });
  const [pincodeCustomersModal, setPincodeCustomersModal] = useState({
    open: false,
    pincode: '',
    rows: [],
  });
  const [breakdownCustomersModal, setBreakdownCustomersModal] = useState({
    open: false,
    title: '',
    rows: [],
  });
  const [pincodeEdits, setPincodeEdits] = useState({}); // requestId -> input value
  const [savingPincodeId, setSavingPincodeId] = useState(null);
  const [savePincodeErrorById, setSavePincodeErrorById] = useState({}); // requestId -> error message
  const [isSavingPincode, setIsSavingPincode] = useState(false);
  const [savePincodeMappingError, setSavePincodeMappingError] = useState('');
  const [savePincodeMappingSuccess, setSavePincodeMappingSuccess] = useState('');

  const pad2 = (n) => String(n).padStart(2, '0');

  // Simpler date & time inputs (easier than native datetime-local in some locales)
  const toDateInputValue = (date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const yyyy = date.getFullYear();
    const mm = pad2(date.getMonth() + 1);
    const dd = pad2(date.getDate());
    return `${yyyy}-${mm}-${dd}`;
  };

  const toTimeInputValue = (date) => {
    if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const hh = pad2(date.getHours());
    const min = pad2(date.getMinutes());
    return `${hh}:${min}`;
  };

  const applyDatePart = (base, dateStr) => {
    if (!base || !(base instanceof Date) || Number.isNaN(base.getTime())) return null;
    if (!dateStr || typeof dateStr !== 'string') return null;
    const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
    if (![y, m, d].every((v) => Number.isFinite(v))) return null;
    return new Date(y, (m || 1) - 1, d || 1, base.getHours(), base.getMinutes(), 0, 0);
  };

  const applyTimePart = (base, timeStr) => {
    if (!base || !(base instanceof Date) || Number.isNaN(base.getTime())) return null;
    if (!timeStr || typeof timeStr !== 'string') return null;
    const [hh, min] = timeStr.split(':').map((v) => parseInt(v, 10));
    if (![hh, min].every((v) => Number.isFinite(v))) return null;
    return new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh || 0, min || 0, 0, 0);
  };

  const openDateTimeModal = () => {
    setDatePickerError('');
    setDatePickerDraft({
      startDate: dateRange?.startDate instanceof Date ? dateRange.startDate : new Date(),
      endDate: dateRange?.endDate instanceof Date ? dateRange.endDate : new Date(),
    });
    setShowDatePicker(true);
  };

  const closeDateTimeModal = () => {
    setShowDatePicker(false);
    setDatePickerError('');
  };

  const setPreset = (preset) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    if (preset === 'today') {
      setDatePickerDraft({ startDate: startOfToday, endDate: now });
      return;
    }
    if (preset === 'yesterday') {
      const y = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
      const end = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 0, 0);
      setDatePickerDraft({ startDate: y, endDate: end });
      return;
    }
    if (preset === 'last7') {
      const start = new Date(startOfToday.getTime() - 6 * 24 * 60 * 60 * 1000);
      setDatePickerDraft({ startDate: start, endDate: now });
      return;
    }
  };

  const normalizePincode = (value) => {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) return '';
    // Commonly 6-digit in India; keep first 6 if longer
    return digits.length >= 6 ? digits.slice(0, 6) : digits;
  };

  const normalizeLabel = (value) => String(value || '').trim().toLowerCase();

  const extractPincodeFromText = (text) => {
    const s = String(text || '');
    // Prefer 6 digit pin if present anywhere in the string (India)
    const m6 = s.match(/\b(\d{6})\b/);
    if (m6?.[1]) return m6[1];
    // Fallback: any 5+ digits group (last resort)
    const m5 = s.match(/\b(\d{5,10})\b/);
    if (m5?.[1]) return normalizePincode(m5[1]);
    return '';
  };

  const extractPincodeFromRow = (r) => {
    // Prefer explicit pincode column
    const direct = normalizePincode(r?.pincode);
    if (direct) return direct;

    // Fallback: try to derive from other fields (address/location/drop_location etc.)
    return (
      extractPincodeFromText(r?.address) ||
      extractPincodeFromText(r?.drop_location) ||
      extractPincodeFromText(r?.location) ||
      extractPincodeFromText(r?.mechanic_location) ||
      ''
    );
  };

  const toTitleCase = (value) => {
    const s = String(value || '').trim();
    if (!s) return '';
    return s
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (!editPincode.open) {
      setDistrictSuggestions([]);
      setStateSuggestions([]);
    }
  }, [editPincode.open]);

  useEffect(() => {
    if (!editPincode.open) return;
    const q = String(editPincode.district || '').trim();
    if (q.length < 2) {
      setDistrictSuggestions([]);
      return;
    }

    const handle = setTimeout(async () => {
      setIsLoadingDistrictSuggestions(true);
      try {
        const { data, error } = await supabase
          .from('pincode_city_state')
          .select('district')
          .not('district', 'is', null)
          .ilike('district', `%${q}%`)
          .limit(25);
        if (error) return;
        const uniq = Array.from(
          new Set((data || []).map((r) => String(r.district || '').trim()).filter(Boolean))
        ).slice(0, 10);
        setDistrictSuggestions(uniq);
      } finally {
        setIsLoadingDistrictSuggestions(false);
      }
    }, 200);

    return () => clearTimeout(handle);
  }, [editPincode.open, editPincode.district]);

  useEffect(() => {
    if (!editPincode.open) return;
    const q = String(editPincode.state || '').trim();
    if (q.length < 2) {
      setStateSuggestions([]);
      return;
    }

    const handle = setTimeout(async () => {
      setIsLoadingStateSuggestions(true);
      try {
        const { data, error } = await supabase
          .from('pincode_city_state')
          .select('state')
          .not('state', 'is', null)
          .ilike('state', `%${q}%`)
          .limit(25);
        if (error) return;
        const uniq = Array.from(
          new Set((data || []).map((r) => String(r.state || '').trim()).filter(Boolean))
        ).slice(0, 10);
        setStateSuggestions(uniq);
      } finally {
        setIsLoadingStateSuggestions(false);
      }
    }, 200);

    return () => clearTimeout(handle);
  }, [editPincode.open, editPincode.state]);

  const chunkArray = (arr, chunkSize) => {
    const out = [];
    for (let i = 0; i < arr.length; i += chunkSize) out.push(arr.slice(i, i + chunkSize));
    return out;
  };

  const { data: rawServiceData, isLoading, refetch } = useQuery({
    queryKey: ['serviceReport', dateRange],
    queryFn: async () => {
      const startMs = dateRange?.startDate instanceof Date ? dateRange.startDate.getTime() : NaN;
      const endMs = dateRange?.endDate instanceof Date ? dateRange.endDate.getTime() : NaN;

      // Get service requests data
      const { data: rpcData, error } = await supabase.rpc('admin_get_service_reports_data');
      
      if (error) {
        console.error('Error loading service data:', error);
        return {
          total: 0,
          resolved: 0,
          pending: 0,
          cancelled: 0,
          avgResolutionHours: 0,
          resolutionRate: 0,
          todayCount: 0,
          urgentCount: 0,
          departments: [],
          topPerformers: [],
          trends: [],
          mechanicPerf: [],
          revenueMetrics: {
            totalQuoted: 0,
            totalMechanicPayment: 0,
            totalAdvancePayment: 0,
            totalPaymentReceived: 0,
            companyProfit: 0
          }
        };
      }

      const allRows = rpcData || [];
      const filteredRows = allRows.filter((r) => {
        const t = new Date(r.complaint_registered_at || r.requested_at || r.created_at).getTime();
        if (!Number.isFinite(t)) return false;
        if (Number.isFinite(startMs) && t < startMs) return false;
        if (Number.isFinite(endMs) && t > endMs) return false;
        return true;
      });

      // Enrich rows with district/state using pincode_city_state table
      const uniquePincodes = Array.from(
        new Set(
          filteredRows
            .map((r) => extractPincodeFromRow(r))
            .filter((p) => p && p.length >= 5)
        )
      );

      const pincodeToLocation = new Map(); // pincode -> { district, state }
      if (uniquePincodes.length > 0) {
        try {
          const batches = chunkArray(uniquePincodes, 500);
          for (const batch of batches) {
            const { data: locRows, error: locErr } = await supabase
              .from('pincode_city_state')
              .select('pincode,district,state')
              .in('pincode', batch);
            if (locErr) throw locErr;
            (locRows || []).forEach((lr) => {
              const pin = normalizePincode(lr.pincode);
              if (!pin) return;
              if (!pincodeToLocation.has(pin)) {
                pincodeToLocation.set(pin, {
                  district: lr.district || '',
                  state: lr.state || '',
                });
              }
            });
          }
        } catch (e) {
          console.warn('Failed loading pincode_city_state mapping:', e);
        }
      }

      const enrichedRows = filteredRows.map((r) => {
        const pin = extractPincodeFromRow(r);
        const loc = pin ? pincodeToLocation.get(pin) : null;
        return {
          ...r,
          __pincode_norm: pin,
          __district: loc?.district || '',
          __state: loc?.state || '',
        };
      });

      const total = filteredRows.length;
      const resolved = enrichedRows.filter(r => String(r.request_status || '').toLowerCase() === 'completed').length;
      const pending = enrichedRows.filter(r => {
        const status = String(r.request_status || '').toLowerCase();
        return !['completed', 'cancelled'].includes(status);
      }).length;
      const cancelled = enrichedRows.filter(r => String(r.request_status || '').toLowerCase() === 'cancelled').length;

      const completedRows = enrichedRows.filter(r => 
        String(r.request_status || '').toLowerCase() === 'completed' && r.mechanic_completed_datetime
      );

      // Calculate average resolution hours
      const durations = completedRows.map(r => {
        const start = new Date(r.complaint_registered_at || r.requested_at || r.created_at).getTime();
        const end = new Date(r.mechanic_completed_datetime).getTime();
        return (end - start) / (1000 * 60 * 60);
      }).filter(v => v >= 0 && Number.isFinite(v));

      const avgResolutionHours = durations.length > 0
        ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10
        : 0;

      const resolutionRate = total > 0 ? Math.round((resolved / total) * 100) : 0;

      // Department breakdown
      const deptMap = {};
      enrichedRows.forEach((r) => {
        const key = r.service_type || 'General';
        if (!deptMap[key]) deptMap[key] = { total: 0, resolved: 0, revenue: 0, mechanicPayment: 0 };
        deptMap[key].total += 1;
        if (String(r.request_status || '').toLowerCase() === 'completed') {
          deptMap[key].resolved += 1;
          deptMap[key].revenue += Number(r.customer_quoted_amount) || 0;
          deptMap[key].mechanicPayment += Number(r.payment_mechanic) || 0;
        }
      });

      const departments = Object.entries(deptMap)
        .map(([name, v]) => ({
          name,
          total: v.total,
          resolved: v.resolved,
          revenue: v.revenue,
          mechanicPayment: v.mechanicPayment,
          profit: (Number(v.revenue) || 0) - (Number(v.mechanicPayment) || 0),
          rate: v.total > 0 ? Math.round((v.resolved / v.total) * 100) : 0,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 6);

      // Revenue metrics
      const revenueMetrics = {
        totalQuoted: enrichedRows.reduce((sum, r) => sum + (Number(r.customer_quoted_amount) || 0), 0),
        totalMechanicPayment: enrichedRows.reduce((sum, r) => sum + (Number(r.payment_mechanic) || 0), 0),
        totalAdvancePayment: enrichedRows.reduce((sum, r) => sum + (Number(r.advance_payment) || 0), 0),
        totalPaymentReceived: enrichedRows.reduce((sum, r) => sum + (Number(r.payment_received) || 0), 0),
        companyProfit: 0
      };
      revenueMetrics.companyProfit = revenueMetrics.totalQuoted - revenueMetrics.totalMechanicPayment;

      const buildBreakdown = (rows, keyGetter, opts = {}) => {
        const map = {};
        rows.forEach((r) => {
          const raw = keyGetter(r) || 'Unknown';
          const norm = opts.caseInsensitive ? (normalizeLabel(raw) || 'unknown') : raw;
          if (!map[norm]) {
            map[norm] = {
              name: opts.caseInsensitive ? (opts.titleCase ? toTitleCase(raw) : String(raw || 'Unknown')) : raw,
              total: 0,
              resolved: 0,
              revenue: 0,
              mechanicPayment: 0,
            };
          } else if (opts.caseInsensitive && opts.titleCase && raw) {
            // Prefer a nicer display value if we encounter a better-cased variant
            const candidate = toTitleCase(raw);
            if (candidate && candidate.length > map[norm].name.length) map[norm].name = candidate;
          }

          map[norm].total += 1;
          if (String(r.request_status || '').toLowerCase() === 'completed') {
            map[norm].resolved += 1;
            map[norm].revenue += Number(r.customer_quoted_amount) || 0;
            map[norm].mechanicPayment += Number(r.payment_mechanic) || 0;
          }
        });
        return Object.values(map)
          .map((v) => ({
            name: v.name || 'Unknown',
            total: v.total,
            resolved: v.resolved,
            revenue: v.revenue,
            mechanicPayment: v.mechanicPayment,
            profit: (Number(v.revenue) || 0) - (Number(v.mechanicPayment) || 0),
            rate: v.total > 0 ? Math.round((v.resolved / v.total) * 100) : 0,
          }))
          .sort((a, b) => b.total - a.total);
      };

      const districtBreakdownFull = buildBreakdown(
        enrichedRows,
        (r) => r.__district || 'Unknown',
        { caseInsensitive: true, titleCase: true }
      );
      const stateBreakdownFull = buildBreakdown(
        enrichedRows,
        (r) => r.__state || 'Unknown',
        { caseInsensitive: true, titleCase: true }
      );
      const employeeBreakdown = buildBreakdown(
        enrichedRows,
        (r) => (r.emp_name || r.registered_by_executive_name || '').trim() || 'Unknown'
      );

      const sumBreakdown = (rows) => {
        const totalSum = (rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
        const resolvedSum = (rows || []).reduce((s, r) => s + (Number(r.resolved) || 0), 0);
        const revenueSum = (rows || []).reduce((s, r) => s + (Number(r.revenue) || 0), 0);
        const mechanicPaymentSum = (rows || []).reduce((s, r) => s + (Number(r.mechanicPayment) || 0), 0);
        return {
          total: totalSum,
          resolved: resolvedSum,
          revenue: revenueSum,
          mechanicPayment: mechanicPaymentSum,
          profit: revenueSum - mechanicPaymentSum,
          rate: totalSum > 0 ? Math.round((resolvedSum / totalSum) * 100) : 0,
        };
      };

      const districtTotals = sumBreakdown(districtBreakdownFull);
      const stateTotals = sumBreakdown(stateBreakdownFull);
      const deptTotals = sumBreakdown(departments);

      const missingPincodeRecords = enrichedRows
        .filter((r) => !r.__pincode_norm)
        .map((r) => ({
          id: r.id,
          customer_name: r.customer_name || 'N/A',
          assigned_advisor_name: r.assigned_advisor_name || r.registered_by_executive_name || 'Unknown',
          contact_number: r.contact_number || '',
          service_type: r.service_type || 'General',
          request_status: r.request_status || r.complaint_status || 'Unknown',
          created_at: r.complaint_registered_at || r.requested_at || r.created_at || null,
        }))
        .sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        });

      // Pincode breakdown (for drill-down from district/state)
      const pincodeMap = {};
      enrichedRows.forEach((r) => {
        const pin = r.__pincode_norm || '';
        const key = pin || '__missing__';
        if (!pincodeMap[key]) {
          pincodeMap[key] = {
            pincode: pin || 'N/A',
            district: r.__district || '',
            state: r.__state || '',
            total: 0,
            resolved: 0,
            revenue: 0,
          };
        }
        pincodeMap[key].total += 1;
        if (String(r.request_status || '').toLowerCase() === 'completed') {
          pincodeMap[key].resolved += 1;
          pincodeMap[key].revenue += Number(r.customer_quoted_amount) || 0;
        }
        // In case some rows have mapping and others don't, keep first non-empty values
        if (!pincodeMap[key].district && r.__district) pincodeMap[key].district = r.__district;
        if (!pincodeMap[key].state && r.__state) pincodeMap[key].state = r.__state;
      });

      const pincodeBreakdown = Object.values(pincodeMap).sort((a, b) => b.total - a.total);

      return {
        total,
        resolved,
        pending,
        cancelled,
        avgResolutionHours,
        resolutionRate,
        todayCount: 0,
        urgentCount: enrichedRows.filter(r => String(r.priority || '').toLowerCase() === 'high').length,
        rows: enrichedRows,
        departments,
        topPerformers: [],
        trends: [],
        mechanicPerf: [],
        revenueMetrics,
        districtBreakdown: districtBreakdownFull.slice(0, 10),
        stateBreakdown: stateBreakdownFull.slice(0, 10),
        districtTotals,
        stateTotals,
        deptTotals,
        employeeBreakdown: employeeBreakdown.slice(0, 10),
        pincodeBreakdown,
        missingPincodeRecords,
      };
    }
  });

  // =========================
  // Status handling (normalize + known list)
  // =========================
  // We show filters for all statuses used in the app/DB (even if current date-range has 0 rows),
  // and we normalize variants like "Cancel"/"canceled" -> "cancelled".
  const normalizeStatusKey = (raw) => {
    let s = String(raw ?? '').trim().toLowerCase();
    if (!s) return 'unknown';
    s = s.replace(/\s+/g, '_').replace(/-+/g, '_');
    s = s.replace(/[^a-z0-9_]/g, '');
    if (!s) return 'unknown';
    if (s === 'cancel' || s === 'canceled' || s === 'cancelled') return 'cancelled';
    if (s === 'complete' || s === 'completed') return 'completed';
    if (s === 'inprogress') return 'in_progress';
    if (s === 'inprocess') return 'in_progress';
    return s;
  };

  const getRowStatusRaw = (r) => {
    const s = String(r?.request_status || r?.complaint_status || 'Unknown').trim();
    return s || 'Unknown';
  };

  // Prefer request_status; fallback to complaint_status if request_status is missing
  const getRowStatusKey = (r) => normalizeStatusKey(r?.request_status || r?.complaint_status || 'unknown');

  const statusLabel = (key) => {
    const k = normalizeStatusKey(key);
    if (k === 'unknown') return 'Unknown';
    return String(k)
      .split('_')
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // request_status: pending | assigned | in_progress | completed | cancelled (+ sometimes registered)
  // complaint_status: registered | assigned_to_advisor | assigned_to_mechanic | in_progress | completed | closed (+ others)
  const KNOWN_STATUS_KEYS = [
    'pending',
    'registered',
    'assigned',
    'assigned_to_advisor',
    'assigned_to_mechanic',
    'dispatched',
    'reached',
    'in_progress',
    'completed',
    'cancelled',
    'closed',
    'unknown',
  ].map(normalizeStatusKey);

  const availableStatuses = useMemo(() => {
    const rows = rawServiceData?.rows || [];
    const fromData = Array.from(new Set(rows.map(getRowStatusKey))).filter(Boolean);
    const uniq = Array.from(new Set([...KNOWN_STATUS_KEYS, ...fromData])).filter(Boolean);
    // Nice ordering: common statuses first, then alphabetical, Unknown last
    const priority = [
      'pending',
      'registered',
      'assigned',
      'assigned_to_advisor',
      'assigned_to_mechanic',
      'dispatched',
      'reached',
      'in_progress',
      'completed',
      'cancelled',
      'closed',
      'unknown',
    ];
    const score = (v) => {
      const key = normalizeStatusKey(v);
      const idx = priority.indexOf(key);
      return idx === -1 ? 999 : idx;
    };
    return uniq.sort((a, b) => {
      const sa = score(a);
      const sb = score(b);
      if (sa !== sb) return sa - sb;
      return String(a).localeCompare(String(b));
    });
  }, [rawServiceData?.rows]);

  useEffect(() => {
    // Initialize from localStorage (or default to all)
    if (!availableStatuses || availableStatuses.length === 0) return;
    try {
      const raw = localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      // Migrate old saved values (which may be raw labels) into normalized keys
      const savedArr = Array.isArray(saved) ? saved.map((s) => normalizeStatusKey(String(s))) : null;
      const allowed = new Set(availableStatuses);
      let next = savedArr ? savedArr.filter((s) => allowed.has(s)) : null;
      if (!next || next.length === 0) next = [...availableStatuses];
      setSelectedStatuses(next);
    } catch {
      setSelectedStatuses([...availableStatuses]);
    }
  }, [availableStatuses.join('|')]);

  useEffect(() => {
    if (!selectedStatuses) return;
    try {
      localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(selectedStatuses));
    } catch {}
  }, [selectedStatuses]);

  useEffect(() => {
    if (!statusFilterOpen) return;
    const onDown = (e) => {
      const el = statusFilterRef.current;
      if (!el) return;
      if (!el.contains(e.target)) setStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [statusFilterOpen]);

  const selectedStatusSet = useMemo(() => new Set(selectedStatuses || []), [selectedStatuses]);

  const rowsForReport = useMemo(() => {
    const rows = rawServiceData?.rows || [];
    if (!selectedStatuses) return rows; // not initialized yet
    return rows.filter((r) => selectedStatusSet.has(getRowStatusKey(r)));
  }, [rawServiceData?.rows, selectedStatuses, selectedStatusSet]);

  const computeReportFromRows = (enrichedRows) => {
    const total = (enrichedRows || []).length;
    const statusLower = (r) => String(getRowStatusKey(r) || '').toLowerCase();

    const resolved = enrichedRows.filter((r) => statusLower(r) === 'completed').length;
    const pending = enrichedRows.filter((r) => {
      const st = statusLower(r);
      return !['completed', 'cancelled', 'canceled'].includes(st);
    }).length;
    const cancelled = enrichedRows.filter((r) => ['cancelled', 'canceled'].includes(statusLower(r))).length;

    const completedRows = enrichedRows.filter(
      (r) => statusLower(r) === 'completed' && r.mechanic_completed_datetime
    );

    // Calculate average resolution hours
    const durations = completedRows
      .map((r) => {
        const start = new Date(r.complaint_registered_at || r.requested_at || r.created_at).getTime();
        const end = new Date(r.mechanic_completed_datetime).getTime();
        return (end - start) / (1000 * 60 * 60);
      })
      .filter((v) => v >= 0 && Number.isFinite(v));

    const avgResolutionHours =
      durations.length > 0
        ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10
        : 0;

    const resolutionRate = total > 0 ? Math.round((resolved / total) * 100) : 0;

    // Department breakdown
    const deptMap = {};
    enrichedRows.forEach((r) => {
      const key = r.service_type || 'General';
      if (!deptMap[key]) deptMap[key] = { total: 0, resolved: 0, revenue: 0, mechanicPayment: 0 };
      deptMap[key].total += 1;
      if (statusLower(r) === 'completed') {
        deptMap[key].resolved += 1;
        deptMap[key].revenue += Number(r.customer_quoted_amount) || 0;
        deptMap[key].mechanicPayment += Number(r.payment_mechanic) || 0;
      }
    });

    const departments = Object.entries(deptMap)
      .map(([name, v]) => ({
        name,
        total: v.total,
        resolved: v.resolved,
        revenue: v.revenue,
        mechanicPayment: v.mechanicPayment,
        profit: (Number(v.revenue) || 0) - (Number(v.mechanicPayment) || 0),
        rate: v.total > 0 ? Math.round((v.resolved / v.total) * 100) : 0,
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 6);

    // Revenue metrics
    const revenueMetrics = {
      totalQuoted: enrichedRows.reduce((sum, r) => sum + (Number(r.customer_quoted_amount) || 0), 0),
      totalMechanicPayment: enrichedRows.reduce((sum, r) => sum + (Number(r.payment_mechanic) || 0), 0),
      totalAdvancePayment: enrichedRows.reduce((sum, r) => sum + (Number(r.advance_payment) || 0), 0),
      totalPaymentReceived: enrichedRows.reduce((sum, r) => sum + (Number(r.payment_received) || 0), 0),
      companyProfit: 0,
    };
    revenueMetrics.companyProfit = revenueMetrics.totalQuoted - revenueMetrics.totalMechanicPayment;

    const buildBreakdown = (rows, keyGetter, opts = {}) => {
      const map = {};
      rows.forEach((r) => {
        const raw = keyGetter(r) || 'Unknown';
        const norm = opts.caseInsensitive ? (normalizeLabel(raw) || 'unknown') : raw;
        if (!map[norm]) {
          map[norm] = {
            name: opts.caseInsensitive ? (opts.titleCase ? toTitleCase(raw) : String(raw || 'Unknown')) : raw,
            total: 0,
            resolved: 0,
            revenue: 0,
            mechanicPayment: 0,
          };
        } else if (opts.caseInsensitive && opts.titleCase && raw) {
          const candidate = toTitleCase(raw);
          if (candidate && candidate.length > map[norm].name.length) map[norm].name = candidate;
        }
        map[norm].total += 1;
        if (statusLower(r) === 'completed') {
          map[norm].resolved += 1;
          map[norm].revenue += Number(r.customer_quoted_amount) || 0;
          map[norm].mechanicPayment += Number(r.payment_mechanic) || 0;
        }
      });
      return Object.values(map)
        .map((v) => ({
          name: v.name || 'Unknown',
          total: v.total,
          resolved: v.resolved,
          revenue: v.revenue,
          mechanicPayment: v.mechanicPayment,
          profit: (Number(v.revenue) || 0) - (Number(v.mechanicPayment) || 0),
          rate: v.total > 0 ? Math.round((v.resolved / v.total) * 100) : 0,
        }))
        .sort((a, b) => b.total - a.total);
    };

    const districtBreakdownFull = buildBreakdown(enrichedRows, (r) => r.__district || 'Unknown', {
      caseInsensitive: true,
      titleCase: true,
    });
    const stateBreakdownFull = buildBreakdown(enrichedRows, (r) => r.__state || 'Unknown', {
      caseInsensitive: true,
      titleCase: true,
    });
    const employeeBreakdown = buildBreakdown(enrichedRows, (r) => (r.emp_name || r.registered_by_executive_name || '').trim() || 'Unknown');

    const sumBreakdown = (rows) => {
      const totalSum = (rows || []).reduce((s, r) => s + (Number(r.total) || 0), 0);
      const resolvedSum = (rows || []).reduce((s, r) => s + (Number(r.resolved) || 0), 0);
      const revenueSum = (rows || []).reduce((s, r) => s + (Number(r.revenue) || 0), 0);
      const mechanicPaymentSum = (rows || []).reduce((s, r) => s + (Number(r.mechanicPayment) || 0), 0);
      return {
        total: totalSum,
        resolved: resolvedSum,
        revenue: revenueSum,
        mechanicPayment: mechanicPaymentSum,
        profit: revenueSum - mechanicPaymentSum,
        rate: totalSum > 0 ? Math.round((resolvedSum / totalSum) * 100) : 0,
      };
    };

    const districtTotals = sumBreakdown(districtBreakdownFull);
    const stateTotals = sumBreakdown(stateBreakdownFull);
    const deptTotals = sumBreakdown(departments);

    const missingPincodeRecords = enrichedRows
      .filter((r) => !r.__pincode_norm)
      .map((r) => ({
        id: r.id,
        customer_name: r.customer_name || 'N/A',
        assigned_advisor_name: r.assigned_advisor_name || r.registered_by_executive_name || 'Unknown',
        contact_number: r.contact_number || '',
        service_type: r.service_type || 'General',
        request_status: getRowStatusRaw(r),
        created_at: r.complaint_registered_at || r.requested_at || r.created_at || null,
      }))
      .sort((a, b) => {
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        return tb - ta;
      });

    // Pincode breakdown (for drill-down from district/state)
    const pincodeMap = {};
    enrichedRows.forEach((r) => {
      const pin = r.__pincode_norm || '';
      const key = pin || '__missing__';
      if (!pincodeMap[key]) {
        pincodeMap[key] = {
          pincode: pin || 'N/A',
          district: r.__district || '',
          state: r.__state || '',
          total: 0,
          resolved: 0,
          revenue: 0,
        };
      }
      pincodeMap[key].total += 1;
      if (statusLower(r) === 'completed') {
        pincodeMap[key].resolved += 1;
        pincodeMap[key].revenue += Number(r.customer_quoted_amount) || 0;
      }
      if (!pincodeMap[key].district && r.__district) pincodeMap[key].district = r.__district;
      if (!pincodeMap[key].state && r.__state) pincodeMap[key].state = r.__state;
    });
    const pincodeBreakdown = Object.values(pincodeMap).sort((a, b) => b.total - a.total);

    return {
      total,
      resolved,
      pending,
      cancelled,
      avgResolutionHours,
      resolutionRate,
      todayCount: 0,
      urgentCount: enrichedRows.filter((r) => String(r.priority || '').toLowerCase() === 'high').length,
      rows: enrichedRows,
      departments,
      topPerformers: [],
      trends: [],
      mechanicPerf: [],
      revenueMetrics,
      districtBreakdown: districtBreakdownFull.slice(0, 10),
      stateBreakdown: stateBreakdownFull.slice(0, 10),
      districtTotals,
      stateTotals,
      deptTotals,
      employeeBreakdown: employeeBreakdown.slice(0, 10),
      pincodeBreakdown,
      missingPincodeRecords,
    };
  };

  const serviceData = useMemo(() => {
    if (!rawServiceData) return rawServiceData;
    return computeReportFromRows(rowsForReport);
  }, [rawServiceData, rowsForReport]);

  const formatCurrency = (value) => `₹${Math.round(value || 0).toLocaleString('en-IN')}`;

  // =========================
  // CSV Export (honors date + status filters)
  // =========================
  const csvEscape = (val) => {
    if (val === null || val === undefined) return '';
    let s = '';
    if (typeof val === 'string') s = val;
    else if (typeof val === 'number' || typeof val === 'boolean') s = String(val);
    else s = JSON.stringify(val);
    // Normalize newlines
    s = s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    // Escape quotes
    if (s.includes('"')) s = s.replace(/"/g, '""');
    // Quote if needed
    if (/[",\n]/.test(s)) s = `"${s}"`;
    return s;
  };

  const EXPORT_COLUMNS = [
    // service_requests core columns (as used in your CSV header)
    'id',
    'customer_id',
    'vehicle_id',
    'service_type',
    'priority',
    'request_status',
    'requested_at',
    'expected_completion_date',
    'mechanic_assigned_datetime',
    'mechanic_reached_datetime',
    'mechanic_completed_datetime',
    'assigned_mechanic_id',
    'assigned_mechanic_name',
    'assigned_mechanic_contact',
    'vehicle_number',
    'contact_number',
    'alternate_number',
    'problem',
    'pincode',
    'payment_rcv',
    'payment_mechanic',
    'emp_name',
    'source',
    'remark',
    'register_datetime',
    'is_premium',
    'advance_payment',
    'assigned_remark',
    'dispatch_remark',
    'reached_remark',
    'complete_remark',
    'delete_status',
    'service_tag',
    'card_number',
    'location_link',
    'media_upload',
    'customer_quoted_amount',
    'complaint_registered_at',
    'registered_by_executive_id',
    'registered_by_executive_name',
    'whatsapp_location_sent',
    'whatsapp_location_sent_at',
    'complaint_status',
    'assigned_advisor_id',
    'assigned_advisor_name',
    'assigned_to_advisor_at',
    'customer_name',
    'updated_at',
    'cancelled_at',
    'mechanic_dispatched_datetime',
    'mechanic_started_datetime',
    'mechanic_cancelled_datetime',
    'mechanic_location',
    'mechanic_notes',
    'mechanic_completion_notes',
    'mechanic_cancellation_reason',
    'drop_location',
    'cancelled_remark',
    // derived/enriched fields (helpful)
    '__pincode_norm',
    '__district',
    '__state',
    '__status_key',
  ];

  const handleExportCsv = () => {
    const rows = (serviceData?.rows || []).map((r) => ({
      ...r,
      __status_key: getRowStatusKey(r),
    }));
    const header = EXPORT_COLUMNS.join(',');
    const lines = rows.map((r) => {
      return EXPORT_COLUMNS.map((k) => csvEscape(r?.[k])).join(',');
    });

    const start = dateRange?.startDate instanceof Date ? toDateInputValue(dateRange.startDate) : 'start';
    const end = dateRange?.endDate instanceof Date ? toDateInputValue(dateRange.endDate) : 'end';
    const filename = `service_reports_${start}_to_${end}.csv`;

    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const getPincodeRowsFor = (mode, name) => {
    const all = serviceData?.pincodeBreakdown || [];
    const target = normalizeLabel(name);
    if (target === 'unknown') {
      // Unknown: missing mapping for the chosen dimension
      if (mode === 'state') {
        return all.filter((r) => {
          const s = normalizeLabel(r.state);
          return !s || s === 'unknown';
        });
      }
      return all.filter((r) => {
        const d = normalizeLabel(r.district);
        return !d || d === 'unknown';
      });
    }
    if (mode === 'state') return all.filter((r) => normalizeLabel(r.state) === target);
    return all.filter((r) => normalizeLabel(r.district) === target);
  };

  const openPincodeModal = (mode, label) => {
    const rows = getPincodeRowsFor(mode, label);
    setPincodeModal({ open: true, mode, label, rows });
  };

  const openCustomersForPincode = (pincodeValue) => {
    const pin = normalizePincode(pincodeValue);
    if (!pin || pin === 'N/A') {
      setMissingPincodeModal({ open: true });
      return;
    }
    const all = serviceData?.rows || [];
    const rows = all
      .filter((r) => normalizePincode(r.__pincode_norm) === pin)
      .sort((a, b) => {
        const ta = new Date(a.complaint_registered_at || a.requested_at || a.created_at).getTime();
        const tb = new Date(b.complaint_registered_at || b.requested_at || b.created_at).getTime();
        return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
      })
      .slice(0, 500);
    setPincodeCustomersModal({ open: true, pincode: pin, rows });
  };

  const openCustomersForDepartment = (deptName) => {
    const key = normalizeLabel(deptName || 'General');
    const all = serviceData?.rows || [];
    const rows = all
      .filter((r) => normalizeLabel(r.service_type || 'General') === key)
      .sort((a, b) => {
        const ta = new Date(a.complaint_registered_at || a.requested_at || a.created_at).getTime();
        const tb = new Date(b.complaint_registered_at || b.requested_at || b.created_at).getTime();
        return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
      })
      .slice(0, 500);
    setBreakdownCustomersModal({
      open: true,
      title: `Customers • Department: ${deptName || 'General'}`,
      rows,
    });
  };

  const openCustomersForEmployee = (empName) => {
    const key = normalizeLabel(empName || 'Unknown');
    const all = serviceData?.rows || [];
    const rows = all
      .filter((r) => {
        const raw = (r.emp_name || r.registered_by_executive_name || '').trim() || 'Unknown';
        return normalizeLabel(raw) === key;
      })
      .sort((a, b) => {
        const ta = new Date(a.complaint_registered_at || a.requested_at || a.created_at).getTime();
        const tb = new Date(b.complaint_registered_at || b.requested_at || b.created_at).getTime();
        return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
      })
      .slice(0, 500);
    setBreakdownCustomersModal({
      open: true,
      title: `Customers • Employee: ${empName || 'Unknown'}`,
      rows,
    });
  };

  const openEditForPincode = (row) => {
    if ((row?.pincode || '') === 'N/A') {
      setMissingPincodeModal({ open: true });
      return;
    }
    setEditPincode({
      open: true,
      pincode: row?.pincode || '',
      district: row?.district || '',
      state: row?.state || '',
    });
  };

  const savePincodeMapping = async () => {
    const pin = normalizePincode(editPincode.pincode);
    const district = String(editPincode.district || '').trim();
    const state = String(editPincode.state || '').trim();
    if (!pin) return;
    if (!district && !state) return;

    setIsSavingPincode(true);
    setSavePincodeMappingError('');
    setSavePincodeMappingSuccess('');
    try {
      // Prefer V2 RPC (cache-safe) if available
      const { data: rpcDataV2, error: rpcErrV2 } = await supabase.rpc('admin_upsert_pincode_city_state_v2', {
        p_pincode: pin,
        p_district: district || null,
        p_state: state || null,
      });

      if (rpcErrV2) {
        // Fallback: older RPC name (if V2 SQL not applied yet)
        const { error: rpcErrV1 } = await supabase.rpc('admin_upsert_pincode_city_state', {
          p_pincode: pin,
          p_district: district || null,
          p_state: state || null,
        });
        if (rpcErrV1) {
          throw new Error(
            `Save failed (RPC): ${rpcErrV2.message || 'v2 error'} | ${rpcErrV1.message || 'v1 error'}`
          );
        }
      } else {
        const row = Array.isArray(rpcDataV2) ? rpcDataV2[0] : rpcDataV2;
        if (!row) throw new Error('Save failed: RPC returned empty response. Please re-run latest SQL.');
        if (row.ok === false) throw new Error(row.message || 'Save failed');
        const returnedPin = normalizePincode(row?.pincode);
        if (!returnedPin || returnedPin !== pin) {
          throw new Error('Save failed: pincode not stored. Please re-run latest SQL.');
        }
      }

      // Verify by reading back
      const { data: verify, error: verifyErr } = await supabase
        .from('pincode_city_state')
        .select('pincode,district,state')
        .eq('pincode', pin)
        .order('uuid', { ascending: false })
        .limit(1);
      if (verifyErr) throw verifyErr;
      const v = verify?.[0];
      if (!v?.pincode) throw new Error('Save failed: mapping not found after save.');

      setEditPincode({ open: false, pincode: '', district: '', state: '' });
      setSavePincodeMappingSuccess('Saved successfully.');
      await refetch();
      if (pincodeModal.open) {
        // Refresh modal rows after save
        const rows = getPincodeRowsFor(pincodeModal.mode, pincodeModal.label);
        setPincodeModal((m) => ({ ...m, rows }));
      }
    } catch (e) {
      const msg = e?.message || 'Save failed';
      setSavePincodeMappingError(msg);
    } finally {
      setIsSavingPincode(false);
    }
  };

  const saveRequestPincode = async (requestId) => {
    const raw = pincodeEdits?.[requestId] ?? '';
    const pin = normalizePincode(raw);
    if (!pin || pin.length < 5) return;

    setSavingPincodeId(requestId);
    try {
      setSavePincodeErrorById((prev) => ({ ...(prev || {}), [requestId]: '' }));
      const { data: rpcData, error: rpcErr } = await supabase.rpc('admin_update_service_request_pincode', {
        p_request_id: requestId,
        p_pincode: pin,
      });

      if (rpcErr) {
        throw new Error(
          `RPC error (${rpcErr.code || 'no_code'}): ${rpcErr.message || 'unknown'}${rpcErr.details ? ` — ${rpcErr.details}` : ''}${rpcErr.hint ? ` — ${rpcErr.hint}` : ''}`
        );
      }

      const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
      if (!row) {
        throw new Error('Save failed: RPC returned empty response. Please re-run the latest SQL.');
      }
      if (row.ok === false) {
        throw new Error(row.message || 'Save failed');
      }
      const returnedPin = normalizePincode(row?.pincode);
      if (!returnedPin || returnedPin !== pin) {
        throw new Error('Save failed: pincode not updated. Please re-run the latest SQL.');
      }

      /*
       * If we reached here, DB is updated. Clear input and refetch.
       */

      // clear input for that row
      setPincodeEdits((prev) => {
        const next = { ...(prev || {}) };
        delete next[requestId];
        return next;
      });
      setSavePincodeErrorById((prev) => {
        const next = { ...(prev || {}) };
        delete next[requestId];
        return next;
      });

      await refetch();
    } catch (e) {
      const msg = e?.message || 'Save failed';
      setSavePincodeErrorById((prev) => ({ ...(prev || {}), [requestId]: msg }));
    } finally {
      setSavingPincodeId(null);
    }

    /*
     * Legacy fallback (disabled): direct update is commonly blocked by RLS for anon.
     * Keeping this comment for reference.
     */
    /*
    try {
      const { error: updErr } = await supabase
        .from('service_requests')
        .update({ pincode: pin })
        .eq('id', requestId);
      if (updErr) throw updErr;
      await refetch();
    } catch (e) {}
    */
  };

  /*
   * NOTE: function ends above; below remains unchanged.
   */

  /*
      } else {
        // Fallback: direct update (works if perms/RLS allow)
        const { error: updErr } = await supabase
          .from('service_requests')
          .update({ pincode: pin })
          .eq('id', requestId);
        if (updErr) {
          throw new Error(
            `Save failed. RPC error: ${rpcErr.message || 'unknown'}; Direct update error: ${updErr.message || 'unknown'}`
          );
        }
      }

      // clear input for that row
      setPincodeEdits((prev) => {
        const next = { ...(prev || {}) };
        delete next[requestId];
        return next;
      });
      setSavePincodeErrorById((prev) => {
        const next = { ...(prev || {}) };
        delete next[requestId];
        return next;
      });

      await refetch();
    } catch (e) {
      const msg = e?.message || 'Save failed';
      setSavePincodeErrorById((prev) => ({ ...(prev || {}), [requestId]: msg }));
    } finally {
      setSavingPincodeId(null);
    }
  };
  */

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading service data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Service Reports</h1>
          <p className="text-gray-600 mt-1">Service operations analytics and reporting</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowProfit((v) => !v)}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            title="Toggle company profit visibility"
          >
            <span>{showProfit ? 'Hide Profit' : 'Show Profit'}</span>
          </button>

          {/* Status filter */}
          <div className="relative" ref={statusFilterRef}>
            <button
              onClick={() => setStatusFilterOpen((v) => !v)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Filter by status"
            >
              <span>Status</span>
              <span className="text-xs text-gray-500">
                {selectedStatuses
                  ? `${selectedStatuses.length}/${availableStatuses.length}`
                  : 'All'}
              </span>
              <ChevronDown size={18} />
            </button>
            {statusFilterOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <div className="text-sm font-semibold text-gray-900">Statuses</div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedStatuses([...availableStatuses])}
                      className="text-xs px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50"
                      type="button"
                    >
                      Select all
                    </button>
                    <button
                      onClick={() => setSelectedStatuses([])}
                      className="text-xs px-2 py-1 rounded-md border border-gray-200 hover:bg-gray-50"
                      type="button"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="max-h-72 overflow-auto py-1">
                  {availableStatuses.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500">No statuses</div>
                  ) : (
                    availableStatuses.map((st) => (
                      <label
                        key={st}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedStatusSet.has(st)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setSelectedStatuses((prev) => {
                              const base = Array.isArray(prev) ? prev : [...availableStatuses];
                              const set = new Set(base);
                              if (checked) set.add(st);
                              else set.delete(st);
                              return Array.from(set);
                            });
                          }}
                        />
                        <span className="text-sm text-gray-800">{statusLabel(st)}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={openDateTimeModal}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Calendar size={20} />
            <span>Select Date &amp; Time</span>
          </button>
          <button
            onClick={handleExportCsv}
            disabled={!serviceData?.rows || serviceData.rows.length === 0}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export current filtered results as CSV"
          >
            <Download size={20} />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      {showDatePicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-6"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Select Date &amp; Time</h2>
                <p className="text-xs text-gray-500 mt-0.5">Set the time range to filter service reports.</p>
              </div>
              <button
                onClick={closeDateTimeModal}
                className="p-2 rounded-lg hover:bg-gray-100"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPreset('today')}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Today
                </button>
                <button
                  onClick={() => setPreset('yesterday')}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Yesterday
                </button>
                <button
                  onClick={() => setPreset('last7')}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Last 7 Days
                </button>
              </div>

              {datePickerError ? (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle size={16} className="mt-0.5" />
                  <div>{datePickerError}</div>
                </div>
              ) : null}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">Start</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <input
                        type="date"
                        value={toDateInputValue(datePickerDraft.startDate)}
                        onChange={(e) => {
                          const next = applyDatePart(datePickerDraft.startDate, e.target.value);
                          if (next) setDatePickerDraft((d) => ({ ...d, startDate: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                      <input
                        type="time"
                        value={toTimeInputValue(datePickerDraft.startDate)}
                        onChange={(e) => {
                          const next = applyTimePart(datePickerDraft.startDate, e.target.value);
                          if (next) setDatePickerDraft((d) => ({ ...d, startDate: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <div className="text-sm font-medium text-gray-900 mb-2">End</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                      <input
                        type="date"
                        value={toDateInputValue(datePickerDraft.endDate)}
                        onChange={(e) => {
                          const next = applyDatePart(datePickerDraft.endDate, e.target.value);
                          if (next) setDatePickerDraft((d) => ({ ...d, endDate: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                      <input
                        type="time"
                        value={toTimeInputValue(datePickerDraft.endDate)}
                        onChange={(e) => {
                          const next = applyTimePart(datePickerDraft.endDate, e.target.value);
                          if (next) setDatePickerDraft((d) => ({ ...d, endDate: next }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                onClick={closeDateTimeModal}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const s = datePickerDraft?.startDate instanceof Date ? datePickerDraft.startDate : null;
                  const e = datePickerDraft?.endDate instanceof Date ? datePickerDraft.endDate : null;
                  if (!s || Number.isNaN(s.getTime()) || !e || Number.isNaN(e.getTime())) {
                    setDatePickerError('Please select valid start and end date/time.');
                    return;
                  }
                  // If user accidentally selects reversed range, auto-fix by swapping.
                  if (s.getTime() > e.getTime()) {
                    setDateRange({ startDate: e, endDate: s });
                  } else {
                    setDateRange({ startDate: s, endDate: e });
                  }
                  setShowDatePicker(false);
                  setDatePickerError('');
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Total Requests</span>
            <BarChart3 size={20} className="text-blue-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{serviceData?.total || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Resolved</span>
            <CheckCircle size={20} className="text-green-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{serviceData?.resolved || 0}</p>
          <p className="text-sm text-gray-500 mt-1">
            {serviceData?.resolutionRate || 0}% resolution rate
          </p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Pending</span>
            <Clock size={20} className="text-yellow-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{serviceData?.pending || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Avg Resolution</span>
            <TrendingUp size={20} className="text-purple-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{serviceData?.avgResolutionHours || 0}h</p>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Total Quoted</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(serviceData?.revenueMetrics?.totalQuoted || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Mechanic Payment</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(serviceData?.revenueMetrics?.totalMechanicPayment || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Payment Received</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(serviceData?.revenueMetrics?.totalPaymentReceived || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Advance Payment</p>
          <p className="text-xl font-bold text-gray-900">
            {formatCurrency(serviceData?.revenueMetrics?.totalAdvancePayment || 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600 mb-1">Company Profit</p>
          <p className="text-xl font-bold text-green-600">
            {formatCurrency(serviceData?.revenueMetrics?.companyProfit || 0)}
          </p>
        </div>
      </div>

      {/* Department Breakdown */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Department Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resolved</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mechanic Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {serviceData?.departments?.map((dept, idx) => (
                <tr
                  key={idx}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openCustomersForDepartment(dept.name)}
                  title="Click to view customers"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{dept.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{dept.total}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{dept.resolved}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{dept.rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(dept.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(dept.mechanicPayment)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {showProfit ? formatCurrency(dept.profit) : '••••'}
                  </td>
                </tr>
              ))}
              {(serviceData?.deptTotals?.total || 0) > 0 && (
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{serviceData.deptTotals.total}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{serviceData.deptTotals.resolved}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{serviceData.deptTotals.rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.deptTotals.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.deptTotals.mechanicPayment)}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {showProfit ? formatCurrency(serviceData.deptTotals.profit) : '••••'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Location + Employee Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* District Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">District-wise Breakdown</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resolved</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mechanic Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(serviceData?.districtBreakdown || []).map((row, idx) => (
                  <tr
                    key={idx}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => openPincodeModal('district', row.name)}
                    title="Click to view pincodes"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.total}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.resolved}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.rate}%</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.mechanicPayment)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{showProfit ? formatCurrency(row.profit) : '••••'}</td>
                  </tr>
                ))}
                {(!serviceData?.districtBreakdown || serviceData.districtBreakdown.length === 0) && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>No data</td>
                  </tr>
                )}
                {(serviceData?.districtTotals?.total || 0) > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.districtTotals.total}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.districtTotals.resolved}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.districtTotals.rate}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.districtTotals.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.districtTotals.mechanicPayment)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {showProfit ? formatCurrency(serviceData.districtTotals.profit) : '••••'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* State Breakdown */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">State-wise Breakdown</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resolved</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mechanic Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(serviceData?.stateBreakdown || []).map((row, idx) => (
                  <tr
                    key={idx}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => openPincodeModal('state', row.name)}
                    title="Click to view pincodes"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.total}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.resolved}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.rate}%</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.mechanicPayment)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{showProfit ? formatCurrency(row.profit) : '••••'}</td>
                  </tr>
                ))}
                {(!serviceData?.stateBreakdown || serviceData.stateBreakdown.length === 0) && (
                  <tr>
                    <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>No data</td>
                  </tr>
                )}
                {(serviceData?.stateTotals?.total || 0) > 0 && (
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.stateTotals.total}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.stateTotals.resolved}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{serviceData.stateTotals.rate}%</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.stateTotals.revenue)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatCurrency(serviceData.stateTotals.mechanicPayment)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {showProfit ? formatCurrency(serviceData.stateTotals.profit) : '••••'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Employee Breakdown */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Employee-wise Breakdown</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resolved</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mechanic Payment</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(serviceData?.employeeBreakdown || []).map((row, idx) => (
                <tr
                  key={idx}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => openCustomersForEmployee(row.name)}
                  title="Click to view customers"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.total}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.resolved}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{row.rate}%</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.revenue)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatCurrency(row.mechanicPayment)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{showProfit ? formatCurrency(row.profit) : '••••'}</td>
                </tr>
              ))}
              {(!serviceData?.employeeBreakdown || serviceData.employeeBreakdown.length === 0) && (
                <tr>
                  <td className="px-4 py-6 text-sm text-gray-500" colSpan={7}>No data</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pincode Drilldown Modal */}
      {pincodeModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Pincodes • {pincodeModal.mode === 'state' ? 'State' : 'District'}: {pincodeModal.label}
                </h3>
                <p className="text-sm text-gray-500">
                  Click a pincode to view customers. Use “Set mapping” to fix district/state. For Unknown, please set both if possible.
                </p>
              </div>
              <button
                onClick={() => setPincodeModal({ open: false, mode: null, label: '', rows: [] })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pincode</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">District</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resolved</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(pincodeModal.rows || []).map((r) => (
                      <tr key={r.pincode} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          <button
                            type="button"
                            className="text-primary-700 hover:underline"
                            onClick={() => openCustomersForPincode(r.pincode)}
                            title="View customers for this pincode"
                          >
                            {r.pincode}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.district || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.state || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-600">{r.total}</td>
                        <td className="px-3 py-2 text-sm text-gray-600">{r.resolved}</td>
                        <td className="px-3 py-2 text-sm text-gray-600">{formatCurrency(r.revenue)}</td>
                        <td className="px-3 py-2 text-sm">
                          <button
                            onClick={() => openEditForPincode(r)}
                            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm disabled:opacity-60"
                          >
                            {(r.pincode || '') === 'N/A' ? 'View customers' : 'Set mapping'}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(!pincodeModal.rows || pincodeModal.rows.length === 0) && (
                      <tr>
                        <td className="px-3 py-6 text-sm text-gray-500" colSpan={7}>No pincodes found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pincode → Customers modal */}
      {pincodeCustomersModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Customers • Pincode: {pincodeCustomersModal.pincode}
                </h3>
                <p className="text-sm text-gray-500">
                  Total: {pincodeCustomersModal.rows.length}. Click customer name to open advisor-style complaint details.
                </p>
              </div>
              <button
                onClick={() => setPincodeCustomersModal({ open: false, pincode: '', rows: [] })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Advisor</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(pincodeCustomersModal.rows || []).map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          <button
                            className="text-primary-700 hover:underline"
                            onClick={() => {
                              setPincodeCustomersModal({ open: false, pincode: '', rows: [] });
                              setPincodeModal({ open: false, mode: null, label: '', rows: [] });
                              navigate(`/service_advisor/complaints/${r.id}`);
                            }}
                          >
                            {r.customer_name || 'N/A'}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.assigned_advisor_name || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.contact_number || '—'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.service_type || 'General'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.request_status || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {r.complaint_registered_at || r.requested_at || r.created_at
                            ? new Date(r.complaint_registered_at || r.requested_at || r.created_at).toLocaleString('en-IN')
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <button
                            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                            onClick={() => window.open(`/service_advisor/complaints/${r.id}`, '_blank')}
                          >
                            Open in new tab
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(pincodeCustomersModal.rows || []).length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-sm text-gray-500" colSpan={7}>No records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department/Employee → Customers modal */}
      {breakdownCustomersModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{breakdownCustomersModal.title}</h3>
                <p className="text-sm text-gray-500">
                  Total: {breakdownCustomersModal.rows.length}
                  {breakdownCustomersModal.rows.length >= 500 ? ' (showing latest 500)' : ''}. Click customer name to open advisor-style complaint details.
                </p>
              </div>
              <button
                onClick={() => setBreakdownCustomersModal({ open: false, title: '', rows: [] })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Advisor</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pincode</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(breakdownCustomersModal.rows || []).map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          <button
                            className="text-primary-700 hover:underline"
                            onClick={() => {
                              setBreakdownCustomersModal({ open: false, title: '', rows: [] });
                              navigate(`/service_advisor/complaints/${r.id}`);
                            }}
                          >
                            {r.customer_name || 'N/A'}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.assigned_advisor_name || r.registered_by_executive_name || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.contact_number || '—'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {r.__pincode_norm ? (
                            <button
                              className="text-primary-700 hover:underline"
                              onClick={() => openCustomersForPincode(r.__pincode_norm)}
                              title="View customers for this pincode"
                            >
                              {r.__pincode_norm}
                            </button>
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.service_type || 'General'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.request_status || r.complaint_status || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {r.complaint_registered_at || r.requested_at || r.created_at
                            ? new Date(r.complaint_registered_at || r.requested_at || r.created_at).toLocaleString('en-IN')
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <button
                            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                            onClick={() => window.open(`/service_advisor/complaints/${r.id}`, '_blank')}
                          >
                            Open in new tab
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(breakdownCustomersModal.rows || []).length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-sm text-gray-500" colSpan={8}>No records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Pincode Mapping Modal */}
      {editPincode.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg bg-white rounded-xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Set District/State for Pincode</h3>
              <button
                onClick={() => setEditPincode({ open: false, pincode: '', district: '', state: '' })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-4">
              {!!savePincodeMappingError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {savePincodeMappingError}
                </div>
              )}
              {!!savePincodeMappingSuccess && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                  {savePincodeMappingSuccess}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  value={editPincode.pincode}
                  onChange={(e) => setEditPincode((s) => ({ ...s, pincode: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g. 400001"
                />
                <p className="text-xs text-gray-500 mt-1">We will save first 6 digits.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
                  <input
                    list="district-suggestions"
                    value={editPincode.district}
                    onChange={(e) => setEditPincode((s) => ({ ...s, district: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g. Mumbai"
                  />
                  <datalist id="district-suggestions">
                    {(districtSuggestions || []).map((d) => (
                      <option key={d} value={d} />
                    ))}
                  </datalist>
                  {isLoadingDistrictSuggestions && (
                    <div className="text-[11px] text-gray-400 mt-1">Loading suggestions…</div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                  <input
                    list="state-suggestions"
                    value={editPincode.state}
                    onChange={(e) => setEditPincode((s) => ({ ...s, state: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    placeholder="e.g. Maharashtra"
                  />
                  <datalist id="state-suggestions">
                    {(stateSuggestions || []).map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                  {isLoadingStateSuggestions && (
                    <div className="text-[11px] text-gray-400 mt-1">Loading suggestions…</div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setEditPincode({ open: false, pincode: '', district: '', state: '' })}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                  disabled={isSavingPincode}
                >
                  Cancel
                </button>
                <button
                  onClick={savePincodeMapping}
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 text-sm disabled:opacity-60"
                  disabled={isSavingPincode}
                >
                  {isSavingPincode ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Missing Pincode → Customer/Advisor List */}
      {missingPincodeModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Missing Pincode • Customers</h3>
                <p className="text-sm text-gray-500">
                  Total: {(serviceData?.missingPincodeRecords || []).length}. Click customer name to open reference complaint.
                </p>
              </div>
              <button
                onClick={() => setMissingPincodeModal({ open: false })}
                className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Advisor</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Add Pincode</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {(serviceData?.missingPincodeRecords || []).slice(0, 300).map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">
                          <button
                            className="text-primary-700 hover:underline"
                            onClick={() => {
                              setMissingPincodeModal({ open: false });
                              setPincodeModal({ open: false, mode: null, label: '', rows: [] });
                              navigate(`/service_advisor/complaints/${r.id}`);
                            }}
                          >
                            {r.customer_name || 'N/A'}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.assigned_advisor_name || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.contact_number || '—'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            <input
                              value={pincodeEdits?.[r.id] ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPincodeEdits((prev) => ({ ...(prev || {}), [r.id]: v }));
                                setSavePincodeErrorById((prev) => ({ ...(prev || {}), [r.id]: '' }));
                              }}
                              className="w-28 px-2 py-1 border border-gray-300 rounded-md text-sm"
                              placeholder="6-digit"
                              inputMode="numeric"
                            />
                            <button
                              className="px-2.5 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 text-xs disabled:opacity-60"
                              disabled={
                                savingPincodeId === r.id ||
                                !normalizePincode(pincodeEdits?.[r.id] ?? '') ||
                                normalizePincode(pincodeEdits?.[r.id] ?? '').length < 5
                              }
                              onClick={() => saveRequestPincode(r.id)}
                            >
                              {savingPincodeId === r.id ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            Saves to DB and removes from N/A list.
                          </div>
                          {!!savePincodeErrorById?.[r.id] && (
                            <div className="text-[11px] text-red-600 mt-1">
                              {savePincodeErrorById[r.id]}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.service_type || 'General'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{r.request_status || 'Unknown'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">
                          {r.created_at ? new Date(r.created_at).toLocaleString('en-IN') : '—'}
                        </td>
                        <td className="px-3 py-2 text-sm">
                          <button
                            className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
                            onClick={() => window.open(`/service_advisor/complaints/${r.id}`, '_blank')}
                          >
                            Open in new tab
                          </button>
                        </td>
                      </tr>
                    ))}
                    {(serviceData?.missingPincodeRecords || []).length > 300 && (
                      <tr>
                        <td className="px-3 py-3 text-sm text-gray-500" colSpan={8}>
                          Showing first 300 records. Narrow date range to see fewer.
                        </td>
                      </tr>
                    )}
                    {(serviceData?.missingPincodeRecords || []).length === 0 && (
                      <tr>
                        <td className="px-3 py-6 text-sm text-gray-500" colSpan={8}>No records found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceReport;
